import { ShareIcon, EyeIcon } from "lucide-react";
import { MapPinIcon, ClockIcon } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { convertUTCToLocalTime } from "../../../Common/ReusableFunction";
import { MdOutlineGroups2 } from "react-icons/md";
import { FaRegUser } from "react-icons/fa";
import { useEffect, useState } from "react";
import { RiPinDistanceLine } from "react-icons/ri";

import DealViewDetailsDialog from "./DealViewDetailsDialog"; // ✅ Import the new component
import StarRating from "../../../Common/StarRating";

const DealCard = ({ deal, onClick }) => {
  const [distance, setDistance] = useState(null);
  const [distanceType, setDistanceType] = useState("km");
  const [isDialogOpen, setIsDialogOpen] = useState(false); // Managed within DealCard

  const userLocation = localStorage.getItem("userLocation")
    ? JSON.parse(localStorage.getItem("userLocation"))
    : {};

  const {
    deal_title,
    actual_price,
    deal_price,
    discount_percentage,
    uploaded_images,
    original_images,
    deal_uuid,
    vendor_name,
    location_city,
    location_state,
    location_house_no,
    location_road_name,
    location_pincode,
    location_country,
    end_date,
    end_time,
    view_count,
    deal_views,
    deal_average_rating
  } = deal;

  const imageSrc =
    original_images?.[0] ||
    uploaded_images?.[0] ||
    "https://placehold.co/400x200/cccccc/ffffff?text=No+Image";

  const handleShareClick = () => {
    const url = `${window.location.origin}/DealDetails/${deal_uuid}`;
    if (navigator.share) {
      navigator
        .share({
          title: deal_title || "Check this deal!",
          text: "Check out this deal on UpSwap!",
          url: url,
        })
        .then(() => console.log("Shared successfully"))
        .catch((err) => console.error("Error sharing", err));
    } else {
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success("🔗 Link copied to clipboard!"))
        .catch(() => toast.error("❌ Failed to copy link."));
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Radius of the earth in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(2); // in km
  };

  useEffect(() => {
    const vendorLat = parseFloat(deal.latitude);
    const vendorLng = parseFloat(deal.longitude);
    const userLat = parseFloat(userLocation.latitude);
    const userLng = parseFloat(userLocation.longitude);

    if (!isNaN(vendorLat) && !isNaN(vendorLng)) {
      const d = calculateDistance(userLat, userLng, vendorLat, vendorLng);
      if (userLocation?.country !== "India") {
        const miles = d * 0.621371;
        setDistance(miles?.toFixed(2));
        setDistanceType("miles");
      } else {
        setDistance(d);
        setDistanceType("km");
      }
    }
  }, [
    deal.latitude,
    deal.longitude,
    userLocation.longitude,
    userLocation?.country,
    userLocation?.latitude,
  ]);

  const handleOpenDialog = () => {
    const loggedInVendorId = localStorage.getItem("vendor_id");
    if (!loggedInVendorId) {
      toast.error("Only the vendor who posted the deal can see views.");
      return;
    }

    if (loggedInVendorId !== String(deal.vendor_uuid)) {
      toast.error("This deal's views are only visible to the posting vendor.");
      return;
    }

    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <div
      className="
        border border-gray-200 rounded-lg overflow-hidden
        bg-white shadow-md
        transition-transform duration-200 hover:scale-[1.01]
        flex flex-col
      "
    >
      {/* Deal Image */}
      <div className="relative h-32 sm:h-40 md:h-40 overflow-hidden">
        <img
          src={imageSrc}
          alt={deal_title || "Deal image"}
          className="w-full h-full object-cover rounded-t-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/upswap.png";
          }}
        />
        <div className='absolute bottom-1 left-1' >
          <StarRating size={15} rating={deal_average_rating} />
        </div>
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col justify-between flex-grow">
        {/* Title */}
        <h3 className="font-semibold text-sm sm:text-base leading-tight mb-2 sm:mb-3 line-clamp-2 capitalize sm:h-12">
          {deal_title}
        </h3>

        {/* Vendor Name */}
        <div className="flex items-center gap-1 mb-0.5">
          <FaRegUser className="text-gray-500 w-4 h-4" />
          <p className="text-xs sm:text-sm text-gray-600">{vendor_name}</p>
        </div>

        {/* Location */}
        <div className="relative group text-xs sm:text-sm text-gray-600 flex items-center gap-1 mb-0.5 h-10">
          <MapPinIcon className="w-4 h-4 text-gray-500" />
          <span className="line-clamp-1 min-w-0">
            {location_city}, {location_state}
          </span>
          <div className="absolute top-6 left-0 z-20 w-max max-w-[250px] bg-white border border-gray-200 shadow-lg rounded-md p-3 text-xs opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-all duration-200">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-medium">{location_house_no}</span>,{" "}
              {location_road_name},<br />
              {location_city}, {location_state} - {location_pincode},<br />
              {location_country}
            </p>
          </div>
        </div>

        <p className="flex items-center gap-1 text-xs sm:text-sm text-gray-600 mb-1">
          <RiPinDistanceLine className="w-4 h-4 text-gray-500" />
          {!isNaN(distance) ? (
            <span>
              You are{" "}
              <span className="text-black font-medium text-sm">
                {distance} {distanceType}
              </span>{" "}
              away
            </span>
          ) : (
            <span className="md:text-sm text-xs text-red-700">
              location not detected
            </span>
          )}
        </p>

        {/* Valid Until */}
        <p className="md:text-sm text-xs text-gray-600 flex items-center gap-1 mb-1 mt-2">
          <ClockIcon className="w-4 h-4 text-gray-500" />
          Valid until:{" "}
          <span className="text-orange-500 font-medium">
            {convertUTCToLocalTime(end_date, end_time)}
          </span>
        </p>

        {/* Pricing */}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-1 h-auto">
          <span className="text-lg  text-black font-bold">₹{deal_price}</span>
          <span className="line-through text-xs text-gray-500">
            ₹{actual_price}
          </span>
          <span className="text-xs text-green-500 font-medium block">
            {discount_percentage}% off
          </span>
        </div>

        {/* Views */}
        {deal_views?.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
            <MdOutlineGroups2 className="w-4 h-4 text-gray-500" />
            Views: <span className="text-black font-medium">{view_count}</span>
            <EyeIcon
              className="w-4 h-4 ml-1 text-gray-500 hover:text-orange-500 cursor-pointer"
              onClick={handleOpenDialog}
            />
          </div>
        )}

        {/* Dialog */}
        <DealViewDetailsDialog
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
          dealViews={deal_views}
        />

        {/* Buttons */}
        <div className="mt-auto flex justify-between items-center pt-2">
          <button
            onClick={() => onClick(deal_uuid)}
            className="py-1.5 px-3 bg-orange-500 text-white rounded-md font-medium text-xs sm:text-sm hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
          >
            View Deal
          </button>
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            onClick={handleShareClick}
          >
            <ShareIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealCard;
